import { HttpClient } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import { Observable, map } from 'rxjs';

import { environment } from 'src/environments/environment';
import { OutputFormula } from '../../models/output-formulas/output-formula';
import {
  OutputFormulaCreatePayload,
  OutputFormulaUpdatePayload,
} from '../../models/output-formulas/output-formula-payload';

interface ListResponse {
  outputFormulas: OutputFormula[];
}

interface LatestResponse {
  outputFormula: OutputFormula;
}

interface MutateResponse {
  outputFormula: OutputFormula;
}

interface AuditResponse {
  revisions: OutputFormula[];
}

interface DependentsWarning {
  count: number;
  names: string[];
}

export interface OutputFormulaDeleteResult {
  outputFormula: OutputFormula | null;
  warning: DependentsWarning | null;
}

@Injectable({
  providedIn: 'root',
})
export class OutputFormulaService {
  constructor(private http: HttpClient) {}

  list(organizationId: string): Observable<OutputFormula[]> {
    const url = `${environment.apiBase}/organizations/${organizationId}/output-formulas`;
    if (isDevMode()) {
      console.log('[DEV] [OutputFormulaService] listing', organizationId);
    }
    return this.http
      .get<ListResponse>(url)
      .pipe(map((res: ListResponse) => res.outputFormulas ?? []));
  }

  getLatest(organizationId: string, id: string): Observable<OutputFormula> {
    const url = `${environment.apiBase}/organizations/${organizationId}/output-formulas/${id}/latest`;
    if (isDevMode()) {
      console.log('[DEV] [OutputFormulaService] loading latest', organizationId, id);
    }
    return this.http
      .get<LatestResponse>(url)
      .pipe(map((res: LatestResponse) => res.outputFormula));
  }

  create(
    organizationId: string,
    payload: OutputFormulaCreatePayload,
  ): Observable<OutputFormula> {
    const url = `${environment.apiBase}/organizations/${organizationId}/output-formulas`;
    if (isDevMode()) {
      console.log('[DEV] [OutputFormulaService] creating', organizationId, payload);
    }
    return this.http
      .post<MutateResponse>(url, payload)
      .pipe(map((res: MutateResponse) => res.outputFormula));
  }

  update(
    organizationId: string,
    id: string,
    payload: OutputFormulaUpdatePayload,
  ): Observable<OutputFormula> {
    const url = `${environment.apiBase}/organizations/${organizationId}/output-formulas/${id}`;
    if (isDevMode()) {
      console.log('[DEV] [OutputFormulaService] updating', organizationId, id, payload);
    }
    return this.http
      .put<MutateResponse>(url, payload)
      .pipe(map((res: MutateResponse) => res.outputFormula));
  }

  remove(
    organizationId: string,
    id: string,
    acknowledge: boolean = false,
  ): Observable<OutputFormulaDeleteResult> {
    const url = `${environment.apiBase}/organizations/${organizationId}/output-formulas/${id}`;
    if (isDevMode()) {
      console.log('[DEV] [OutputFormulaService] deleting', organizationId, id, { acknowledge });
    }
    return this.http.delete<OutputFormulaDeleteResult>(url, {
      params: { acknowledge: String(acknowledge) },
    });
  }

  getAudit(organizationId: string, id: string): Observable<OutputFormula[]> {
    const url = `${environment.apiBase}/organizations/${organizationId}/output-formulas/${id}/audit`;
    if (isDevMode()) {
      console.log('[DEV] [OutputFormulaService] loading audit', organizationId, id);
    }
    return this.http.get<AuditResponse>(url).pipe(map((res: AuditResponse) => res.revisions ?? []));
  }
}
